import { nanoid } from 'nanoid';
import { storeToRefs } from 'pinia';
import { useModalStore } from '@/shared/ui/AppModal/store/modal';
import { useUserStore } from '@/entities/user/store';
import { getNameWeekDay } from '@/shared/utils/getNameWeekDay';
import { toKey } from '@/shared/utils/toKey';
import { useConfirmation } from '@/shared/composables/useConfirmation';
import { SMS_CODE_TYPE_CONFIRMATION } from '@/shared/constants/smsCodeConstants';
import { getAppointmentErrorMessage } from '@/entities/appointment/lib/getAppointmentErrorMessage';
import {
  APPOINTMENT_SUCCESS_TITLE,
  APPOINTMENT_SUCCESS_TEXT,
  THANKS_MODAL_AUTO_CLOSE_DELAY,
} from '@/entities/appointment/lib/appointmentConstants';
import type { ILawyerDetail } from '@/entities/lawyer/model/types/lawyer';
import type { ICreateOrderData, OrderDraft } from '@/entities/appointment/model/types';
import { logError, normalizeError, getServerMessage } from '@/shared/lib/errors';
import { VALIDATION_STATUS } from '@/shared/constants/errorConstants';
import { isApiError } from '@/shared/utils/isApiError';

interface ConsultationPayload {
  lawyerInfo?: ILawyerDetail | null;
  date?: string;
  time?: string;
}

export const useConsultationFlow = () => {
  const modalStore = useModalStore();
  const userStore = useUserStore();
  const { isAuth } = storeToRefs(userStore);
  const { setConfig } = useConfirmation();
  const { $appointmentService, $tokenService, $authService } = useNuxtApp();

  const requestConfirmationCode = async (phone: string): Promise<void> => {
    const { data } = await $tokenService.getBKeyToken<{ key: string }>();
    const bKeyToken = toKey(data.key);
    await $authService.getAuthCode(
      { login: phone, type: SMS_CODE_TYPE_CONFIRMATION },
      bKeyToken,
    );
  };

  const toUserErrorMessage = (error: unknown, action: string): string => {
    const appError = normalizeError(error);
    logError(appError, { action });
    if (appError.kind === 'network') return appError.message;
    const serverMessage =
      appError.status === VALIDATION_STATUS && isApiError(error)
        ? getServerMessage(error)
        : '';
    return serverMessage || getAppointmentErrorMessage(appError.status);
  };

  const openDateTime = (lawyer: ILawyerDetail | null): void => {
    modalStore.setModalState('consultation', false);
    modalStore.setModalState('dateTime', true, { lawyerInfo: lawyer });
  };

  const openConsultation = (payload: ConsultationPayload): void => {
    modalStore.setModalState('dateTime', false);
    modalStore.setModalState('consultation', true, {
      lawyerInfo: payload.lawyerInfo,
      date: payload.date,
      time: payload.time,
    });
  };

  const openConfirmation = async (
    lawyer: ILawyerDetail | null,
    phone: string,
    draft: OrderDraft,
  ): Promise<string> => {
    try {
      await requestConfirmationCode(phone);
    } catch (error) {
      return toUserErrorMessage(error, 'consultation:request-code');
    }
    setConfig({
      phone,
      lawyerInfo: lawyer,
      title: 'Запись на бесплатную консультацию',
      label: 'Введите код из SMS для подтверждения записи',
      submitText: 'Подтвердить запись',
      onSubmit: (code: string) => createOrder(draft, { code }),
      onResend: () => requestConfirmationCode(phone),
    });
    modalStore.setModalState('consultation', false);
    modalStore.setModalState('confirmation', true);
    return '';
  };

  const openThanks = (): void => {
    modalStore.setModalState('consultation', false);
    modalStore.setModalState('confirmation', false);
    modalStore.openSuccess({
      title: APPOINTMENT_SUCCESS_TITLE,
      text: APPOINTMENT_SUCCESS_TEXT,
      isAutoClose: true,
      closeDelay: THANKS_MODAL_AUTO_CLOSE_DELAY,
    });
  };

  const formatDateView = (isoDate: string, time: string): string => {
    if (!isoDate) return '';
    const weekDay = getNameWeekDay(new Date(isoDate).getDay());
    const view = isoDate.split('-').reverse().join('.');
    return `${view}(${weekDay}), ${time}`;
  };

  const buildDraft = (params: {
    lawyerId?: number;
    isoDate: string;
    time: string;
    fullName: string;
    email: string;
    phone: string;
    text: string;
  }): OrderDraft | null => {
    if (!params.lawyerId || !params.isoDate || !params.time) return null;
    const [firstName = '', surname = ''] = params.fullName.trim().split(' ');
    return {
      recording_date: `${params.isoDate} ${params.time}:00`,
      name: firstName,
      surname,
      email: params.email,
      phone: params.phone,
      text: params.text,
      lawyer_id: params.lawyerId,
    };
  };

  const createOrder = async (
    draft: OrderDraft,
    options: { code?: string } = {},
  ): Promise<string> => {
    const wasGuest = !isAuth.value;
    const data: ICreateOrderData = { ...draft };
    if (wasGuest) {
      data.code = options.code ?? '';
      data.device_name = nanoid();
    }
    try {
      const { data: res } = await $appointmentService.createOrder(data);
      if (wasGuest && res?.token) {
        await userStore.startSession(res.token);
      }
      openThanks();
      return '';
    } catch (error) {
      return toUserErrorMessage(error, 'consultation:create-order');
    }
  };

  return {
    isAuth,
    openDateTime,
    openConsultation,
    openConfirmation,
    requestConfirmationCode,
    openThanks,
    formatDateView,
    buildDraft,
    createOrder,
  };
};

export default useConsultationFlow;
